import type { Alignment, Placement, Side } from "@floating-ui/react";
import {
  useFloating,
  useHover,
  useClick,
  offset,
  useInteractions,
  arrow,
  FloatingPortal,
  autoPlacement,
  flip,
  shift,
  useMergeRefs,
  size,
} from "@floating-ui/react";
import type { CSSProperties, PropsWithChildren, ReactNode } from "react";
import { useRef, useState, useEffect } from "react";

import { useIsMobile } from "@hooks/useDeviceSize";
import { useIsTouchDevice } from "@hooks/useIsTouchDevice";
import { cn } from "@utils/tailwind.util";

const ARROW_HEIGHT = 7;
const GAP = 8;

// Edge and rotation only — the offset along that edge comes from floating-ui, so the arrow follows
// the trigger when shift() moves the tooltip.
const arrowAnchor: Record<Side, { style: CSSProperties; rotate: string }> = {
  left: { style: { left: "100%" }, rotate: "translateX(-2px) rotate(-90deg)" },
  right: { style: { right: "100%" }, rotate: "translateX(2px) rotate(90deg)" },
  top: { style: { top: "100%" }, rotate: "translateY(-2px)" },
  bottom: { style: { bottom: "100%" }, rotate: "translateY(2px) rotate(180deg)" },
};

type OffsetValue = number | (({ side }: { side: Side }) => number);

export type TooltipProps<T extends React.ElementType> = Omit<React.ComponentProps<T>, "as"> & {
  as?: T;
  arrow?: boolean;
  className?: string;
  event?: "hover" | "click";
  offset?: OffsetValue;
  placement?: Placement;
  mobilePlacement?: Placement;
  root?: HTMLElement;
  tooltipContent: ReactNode;
  tooltipContentClassName?: string;
  variant?: "default" | "dark";
  hideOnMobile?: boolean;
  delay?: { open: number; close: number };
  disabled?: boolean;
  dependsOnTruncated?: boolean;
  minWidthFromTrigger?: boolean;
  maxWidth?: number;
};

export function Tooltip<C extends React.ElementType = "div">({
  as,
  arrow: showArrow,
  children,
  className,
  event = "hover",
  offset: offsetPadding = ARROW_HEIGHT + GAP,
  placement,
  mobilePlacement,
  root = document.body,
  tooltipContent,
  tooltipContentClassName,
  variant = "default",
  hideOnMobile = false,
  delay = { open: 0, close: 100 },
  disabled = false,
  dependsOnTruncated = false,
  minWidthFromTrigger = false,
  maxWidth,
}: PropsWithChildren<TooltipProps<C>>) {
  const isMobile = useIsMobile();
  const isTouchDevice = useIsTouchDevice();

  const arrowRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  const Component = as || "div";
  const textRef = useRef<HTMLElement>(null);

  const checkTruncation = () => {
    if (textRef.current) {
      const innerTextElement = textRef.current.querySelector("div.text-ellipsis");
      if (innerTextElement) {
        const computedStyle = window.getComputedStyle(innerTextElement);
        const hasLineClamp = computedStyle.getPropertyValue("-webkit-line-clamp") !== "none";

        if (hasLineClamp) {
          setIsTruncated(innerTextElement.scrollHeight > innerTextElement.clientHeight);
        } else {
          const { scrollWidth, clientWidth } = innerTextElement;
          setIsTruncated(scrollWidth > clientWidth);
        }
      } else {
        setIsTruncated(false);
      }
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          checkTruncation();
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (textRef.current) {
      observer.observe(textRef.current);
    }

    return () => observer.disconnect();
  }, [children]);

  const { refs, floatingStyles, context, middlewareData } = useFloating({
    open: isOpen,
    strategy: "absolute",
    onOpenChange: setIsOpen,
    placement: isMobile ? mobilePlacement || placement : placement,
    middleware: [
      // support dynamic offset padding based on side (top or bottom)
      offset(({ placement }) => {
        const side = placement.split("-")[0] as Side;
        return typeof offsetPadding === "function" ? offsetPadding({ side }) : offsetPadding;
      }),
      ...(minWidthFromTrigger
        ? [
            size({
              apply({ rects, elements }) {
                Object.assign(elements.floating.style, {
                  minWidth: `${rects.reference.width}px`,
                  maxWidth: maxWidth ? `${maxWidth}px` : undefined,
                });
              },
            }),
          ]
        : []),
      ...(typeof placement === "undefined" ? [autoPlacement()] : [flip()]),
      // Nudges the tooltip back in when it would overflow — the add-in panel is narrow.
      shift({ padding: 8 }),
      // arrow must run after flip/shift so it points at the trigger even when the tooltip was repositioned.
      arrow({ element: arrowRef }),
    ],
  });

  const hover = useHover(context, {
    enabled: !isTouchDevice && event === "hover",
    delay: delay,
  });

  const click = useClick(context, { enabled: isTouchDevice });
  const propsList = isTouchDevice ? [click] : event === "hover" ? [hover] : [hover, click];

  const { getReferenceProps, getFloatingProps } = useInteractions(propsList);

  const [side, _alignment] = context.placement.split("-") as [Side, Alignment];
  const { style: anchorStyle, rotate } = arrowAnchor[side];
  // Top/bottom tooltips slide the arrow horizontally, left/right vertically. Until floating-ui has
  // measured, fall back to centring so the arrow never renders at the tooltip's corner.
  const isVerticalSide = side === "top" || side === "bottom";
  const arrowOffset = isVerticalSide ? middlewareData.arrow?.x : middlewareData.arrow?.y;
  const arrowStyles: CSSProperties = {
    ...anchorStyle,
    ...(isVerticalSide
      ? { left: arrowOffset != null ? `${arrowOffset}px` : "50%" }
      : { top: arrowOffset != null ? `${arrowOffset}px` : "50%" }),
    transform:
      arrowOffset != null
        ? rotate
        : `${isVerticalSide ? "translateX(-50%)" : "translateY(-50%)"} ${rotate}`,
  };

  useEffect(() => {
    if (isTouchDevice && isOpen) {
      const handleOutsideClick = (event: MouseEvent) => {
        if (!refs.floating.current?.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      document.addEventListener("click", handleOutsideClick);
      return () => document.removeEventListener("click", handleOutsideClick);
    }
  }, [isTouchDevice, isOpen, refs.floating]);

  return (
    <Component
      ref={useMergeRefs([refs.setReference, textRef])}
      {...getReferenceProps()}
      className={className}
    >
      {children}

      {isOpen && (!dependsOnTruncated || isTruncated) && (
        <FloatingPortal root={root}>
          <div
            className={cn(
              "relative z-50 mt-1 max-w-lg rounded-lg text-center",
              {
                "bg-white dark:bg-background-primaryDark p-4 ring-1 ring-inset ring-stroke-primary dark:ring-gray-500 dark:drop-shadow-lg":
                  variant === "default",
                "bg-background-inverted px-2 py-1 text-xs text-content-text drop-shadow-md":
                  variant === "dark",
              },
              tooltipContentClassName
            )}
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              ...((hideOnMobile && isMobile) || disabled ? { visibility: "hidden" } : undefined),
            }}
            {...getFloatingProps()}
          >
            {tooltipContent}

            {showArrow && (
              <svg
                ref={arrowRef}
                className={cn("absolute z-10", {
                  " fill-white": variant === "default",
                  "fill-background-inverted": variant === "dark",
                })}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                xmlns="http://www.w3.org/2000/svg"
                style={arrowStyles}
              >
                <path d="M0 0L16 0L9.12853 6.61217C8.33218 7.37848 7.06525 7.35362 6.29956 6.55668L0 0Z" />
              </svg>
            )}
          </div>
        </FloatingPortal>
      )}
    </Component>
  );
}
